import { useTranslation } from 'react-i18next';
import {
  KoobDescriptionList,
  KoobDescriptionListRow,
  KoobSlideOver,
} from '@koob/ui';
import {
  actorLabel,
  AuditLogIcon,
  AuditLogSentence,
  ImpersonationBadge,
  ViaBadge,
} from './AuditLogSummary';

const formatDateTime = (value) =>
  new Date(value).toLocaleString([], {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

/** How an impersonation ended (Koob admins only): logout, expiry, or running. */
const impersonationEnd = (t, log) => {
  if (!('impersonationEnd' in log) || log.outcome !== 'success') return null;
  const end = log.impersonationEnd;
  return end
    ? t(`impersonationEnd.${end.reason}`, { date: formatDateTime(end.at) })
    : t('impersonationEnd.running');
};

// Paths, IDs and user agents have no spaces: let only those break anywhere.
const rowsOf = (rows, breakAll = false) =>
  rows
    .filter(([, value]) => value)
    .map(([label, value]) => (
      <KoobDescriptionListRow
        key={label}
        label={label}
        value={breakAll ? <span className="break-all">{value}</span> : value}
      />
    ));

/**
 * One audit row: what happened, who did it and when. The technical rows
 * (request, route, IP, user agent, payload) only come back for KOOB admins.
 * `showOrganization` follows the timeline: only when entries span several.
 */
export default function AuditLogDetails({ log, onClose, showOrganization }) {
  const { t } = useTranslation('auditLog');

  const technical = log
    ? rowsOf(
        [
          [t('details.statusCode'), log.path && log.statusCode],
          [t('details.request'), log.path && `${log.httpMethod} ${log.path}`],
          [t('details.route'), log.route && `${log.route} (${log.action})`],
          [t('details.requestId'), log.requestId],
          [t('details.ip'), log.ip],
          [t('details.userAgent'), log.userAgent],
        ],
        true,
      )
    : [];

  return (
    <KoobSlideOver
      open={Boolean(log)}
      setOpen={(open) => !open && onClose()}
      title={t('details.title')}
    >
      {log && (
        <div className="flex flex-col gap-6">
          <div className="flex items-start gap-3">
            <AuditLogIcon row={log} />
            <div className="flex flex-col gap-1 pt-1.5">
              <AuditLogSentence row={log} />
              {log.outcome !== 'success' && (
                <span className="text-sm text-gray-500">
                  {t(`outcomeHelp.${log.outcome}`)}
                </span>
              )}
            </div>
          </div>

          <KoobDescriptionList>
            {rowsOf([
              [
                t('details.by'),
                <span key="by" className="flex flex-col items-start gap-1">
                  <span>{actorLabel(t, log)}</span>
                  {log.actor?.name && log.actor.email && (
                    <span className="text-gray-500">{log.actor.email}</span>
                  )}
                  <ImpersonationBadge row={log} />
                  <ViaBadge row={log} />
                </span>,
              ],
              [t('details.when'), formatDateTime(log.occurredAt)],
              [t('details.ended'), impersonationEnd(t, log)],
              [
                t('details.organization'),
                showOrganization && !log.koobSupport && log.organization?.name,
              ],
            ])}
          </KoobDescriptionList>

          {technical.length > 0 && (
            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium text-gray-500">
                {t('details.technical')}
              </span>
              <KoobDescriptionList>{technical}</KoobDescriptionList>
            </div>
          )}

          {'payload' in log && (
            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium text-gray-500">
                {t('details.payload')}
              </span>
              {log.payload ? (
                <pre className="max-h-[50vh] overflow-auto rounded-lg bg-gray-50 p-3 text-xs">
                  {JSON.stringify(log.payload, null, 2)}
                </pre>
              ) : (
                <span className="text-sm text-gray-500">
                  {t('details.noPayload')}
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </KoobSlideOver>
  );
}
